import { useEffect, useState } from "react";

import { cn } from "@/libs/utils";

import { DepartmentItem, SettingUser } from "@/types/setting";

import {
  AddUserToGroup,
  DeleteUserFromGroup,
  GetSearchConditionLiveTextSearch,
  GetUserListByGroupId,
} from "@/services/Setting";

import CommonImg from "@/components/Common/Img";
import CommonButton from "@/components/Common/Button";

import BlackChevronUpIcon from "@/assets/images/black-chevron-icon-up.png";
import BlackChevronDownIcon from "@/assets/images/black-chevron-icon-down.png";
import NoEntryIcon from "@/assets/images/no-entry-icon.png";
import GrayNoEntryIcon from "@/assets/images/Setting/gray-no-entry-icon.png";
import TempProfile from "@/assets/images/LNB/temp-profile.jpg";
import CloseIcon from "@/assets/images/black-close-icon.png";
import BlackSearchIcon from "@/assets/images/black-search-icon.png";
import PlusIcon from "@/assets/images/plus-icon.png";
import TrashBinIcon from "@/assets/images/Setting/trash-bin-icon.png";

type Props = {
  itemIndex: number;
  item: DepartmentItem;
  setItem: React.Dispatch<React.SetStateAction<DepartmentItem[]>>;
  itemListLength: number;
  holdDelete: boolean;
  onChangeTitle: (groupIndex: number, groupId?: string) => void;
  onDelete: (groupId: string) => void;
  onChangeHoldDelete: (value: boolean) => void;
};
export default function CommonDialogDepartmentItem({
  itemIndex,
  item,
  setItem,
  itemListLength,
  holdDelete,
  onChangeTitle,
  onDelete,
  onChangeHoldDelete,
}: Props) {
  const [isOpen, setOpen] = useState<boolean>(false);
  const [isEditing, setEditing] = useState<boolean>(false);
  const [isAdding, setAdding] = useState<boolean>(false);
  const [isDeleting, setDeleting] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [searchUserList, setSearchUserList] = useState<SettingUser[]>([]);
  const [userList, setUserList] = useState<SettingUser[]>([]);
  let debounceTimeout: any;

  const handleLiveSearch = async (value: string) => {
    try {
      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(async () => {
        const result = await GetSearchConditionLiveTextSearch(value);
        setSearchUserList(result.data.pods[0].content);
      }, 100);
    } catch (e) {
    } finally {
    }
  };

  const getUserList = async () => {
    try {
      const params = `group_id=\"${item.group_id}\"`;
      const result = await GetUserListByGroupId(params);
      setUserList(result.data.pods[0].content.items);
    } catch (e) {
    } finally {
    }
  };

  const handleAddUser = async (userId: string) => {
    try {
      const params = `group_id="${item.group_id}", user_id="${userId}"`;
      const result = await AddUserToGroup(params);
      if (result.data.success) {
        getUserList();
        setSearchText("");
        setAdding(false);
      }
    } catch (e) {
    } finally {
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      const params = `group_id="${item.group_id}", user_id="${userId}"`;
      const result = await DeleteUserFromGroup(params);
      if (result.data.success) {
        getUserList();
      }
    } catch (e) {
    } finally {
    }
  };

  useEffect(() => {
    if (isOpen) {
      getUserList();
    }
  }, [isOpen]);

  return (
    <div className="flex flex-col gap-[8px]">
      <div className="flex gap-[12px] items-center pt-[1px]">
        <input
          type="text"
          className="w-full p-[8px] text-sm font-semibold focus:outline-dark-red"
          placeholder="추가할 소속 이름을 입력하세요"
          value={item.group_name}
          onFocus={() => {
            setEditing(true);
          }}
          onChange={(e) => {
            setItem((prev) => [
              ...prev.map((prevItem, prevItemIndex) =>
                prevItemIndex === itemIndex
                  ? {
                      ...prevItem,
                      group_name: e.target.value,
                    }
                  : { ...prevItem }
              ),
            ]);
          }}
        />
        {isEditing && (
          <div className="flex items-center gap-[5px]">
            <CommonButton
              label="취소"
              className="w-[40px] h-fit px-[8px] py-[4px] bg-gray-E border-[1px] border-gray-D rounded-[3px] text-[10px] text-gray-A font-semibold"
              onClick={() => {
                setEditing(false);
              }}
            />
            <CommonButton
              label="적용"
              className="w-[40px] h-fit px-[8px] py-[4px] bg-dark-red border-[1px] border-gray-D rounded-[3px] text-[10px] text-white font-semibold"
              onClick={() => {
                onChangeTitle(itemIndex, item.group_id);
                setEditing(false);
              }}
            />
          </div>
        )}
        {isOpen ? (
          <CommonImg
            src={BlackChevronUpIcon}
            alt="Black Chevron Up"
            className="size-[16px] cursor-pointer"
            onClick={() => {
              setOpen((prev) => !prev);
            }}
          />
        ) : (
          <CommonImg
            src={BlackChevronDownIcon}
            alt="Black Chevron Down"
            className="size-[16px] cursor-pointer"
            onClick={() => {
              setOpen((prev) => !prev);
            }}
          />
        )}
        {isDeleting ? (
          <CommonImg
            src={TrashBinIcon}
            alt="Trash Bin"
            className="size-[20px] cursor-pointer"
            onClick={() => {
              onDelete(item.group_id);
              setDeleting(false);
              onChangeHoldDelete(false);
            }}
          />
        ) : holdDelete ? (
          <CommonImg
            src={GrayNoEntryIcon}
            alt="Disalbed Delete Keyword Group"
            className="size-[20px] cursor-pointer"
          />
        ) : (
          <CommonImg
            src={NoEntryIcon}
            alt="Delete Keyword Group"
            className="size-[20px] cursor-pointer"
            onClick={() => {
              setDeleting(true);
              onChangeHoldDelete(true);
            }}
          />
        )}
      </div>
      {isOpen && (
        <div className="flex gap-[8px] flex-wrap">
          {userList?.map((user, userIndex) => (
            <div
              key={userIndex}
              className="bg-dark-red-clear flex gap-[8px] items-center rounded-[20px] text-gray-A text-[12px] py-[6px] px-[8px]"
            >
              <CommonImg
                src={user.image_url ?? TempProfile}
                alt="Profile Image"
                className="rounded-[50px]"
              />
              <span>
                {user.last_name}
                {user.first_name}
              </span>
              <span className="text-[10px] text-gray-B font-semibold">
                {user.email}
              </span>
              <CommonImg
                src={CloseIcon}
                alt="Close"
                className="size-[4px] cursor-pointer"
                onClick={() => {
                  handleDeleteUser(user.user_id);
                }}
              />
            </div>
          ))}
          {isAdding ? (
            <div className={cn("relative", { "z-10": !!searchUserList })}>
              <div
                className="w-[220px] px-[10px] py-[6px] border-dark-red border-[1px] rounded-[20px] flex justify-between items-center"
                onBlur={() => {
                  let hideResult: any;
                  clearTimeout(hideResult);
                  hideResult = setTimeout(() => {
                    setSearchUserList([]);
                  }, 100);
                }}
              >
                <input
                  type="text"
                  className={cn(
                    "w-full h-[14px] focus:outline-none text-[12px] font-semibold"
                  )}
                  defaultValue={searchText}
                  onFocus={(e) => {
                    handleLiveSearch(e.target.value);
                  }}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    handleLiveSearch(e.target.value);
                  }}
                />
                <CommonImg
                  src={BlackSearchIcon}
                  alt="Search"
                  className="size-[16px]"
                />
              </div>
              {searchUserList.length > 0 && (
                <div className="top-[10px] left-[0px] w-[220px] pt-[20px] z-[-1] absolute flex flex-col bg-white border-x-[1px] border-b-[1px] rounded-b-[4px] text-[12px] font-bold">
                  <div className="max-h-[100px] overflow-auto">
                    {searchUserList.map((user, userIndex) => (
                      <div
                        key={userIndex}
                        onClick={() => {
                          handleAddUser(user.user_id);
                        }}
                      >
                        <div className="p-[8px] flex gap-[8px] justify-center items-center hover:bg-gray-D cursor-pointer">
                          <div>
                            <CommonImg
                              src={TempProfile}
                              alt="Temp"
                              className="size-[18px]"
                            />
                          </div>
                          <div>
                            {user.last_name}
                            {user.first_name}
                          </div>
                          <div className="text-[10px] text-gray-B font-semibold">
                            {user.email}
                          </div>
                        </div>
                        {userIndex < searchUserList.length - 1 && <hr />}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div
              className="w-[220px] px-[8px] py-[6px] flex justify-center items-center rounded-[20px] border-[1px] border-gray-D cursor-pointer"
              onClick={() => {
                setAdding((prev) => !prev);
              }}
            >
              <CommonImg src={PlusIcon} alt="Plus" className="size-[12px]" />
            </div>
          )}
        </div>
      )}

      {itemIndex < itemListLength - 1 && <hr />}
    </div>
  );
}
